import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ChevronRight } from 'lucide-react';
import { KENYA_COUNTIES, SORTED_COUNTIES } from '../constants/locations';

export function MobileSearchBar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);
  const [selectedSubCounty, setSelectedSubCounty] = useState<string | null>(null);

  const [isCountyModalOpen, setIsCountyModalOpen] = useState(false);
  const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);

  const [countySearch, setCountySearch] = useState('');
  const [areaSearch, setAreaSearch] = useState('');

  const countySearchRef = useRef<HTMLInputElement>(null);
  const areaSearchRef = useRef<HTMLInputElement>(null);

  const activeCountyData = KENYA_COUNTIES.find(c => c.name === selectedCounty);

  // Auto-focus search inputs when modals open
  useEffect(() => {
    if (isCountyModalOpen) {
      setCountySearch('');
      setTimeout(() => countySearchRef.current?.focus(), 100);
    }
  }, [isCountyModalOpen]);

  useEffect(() => {
    if (isAreaModalOpen) {
      setAreaSearch('');
      setTimeout(() => areaSearchRef.current?.focus(), 100);
    }
  }, [isAreaModalOpen]);

  const handleSelectCounty = (countyName: string | null) => {
    setSelectedCounty(countyName);
    setSelectedSubCounty(null);
    setIsCountyModalOpen(false);
    setCountySearch('');
  };

  const handleSelectSubCounty = (subName: string) => {
    setSelectedSubCounty(subName);
    setIsAreaModalOpen(false);
    setAreaSearch('');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    if (selectedCounty) params.set('county', selectedCounty);
    if (selectedSubCounty) params.set('subCounty', selectedSubCounty);
    navigate(`/search?${params.toString()}`);
  };

  // Filtered county list based on search
  const filteredCounties = SORTED_COUNTIES.filter(c =>
    c.name.toLowerCase().includes(countySearch.toLowerCase())
  );

  // Filtered sub-county list based on search
  const filteredSubCounties = (activeCountyData?.subcounties ?? []).filter(s =>
    s.name.toLowerCase().includes(areaSearch.toLowerCase())
  );

  return (
    <div className="md:hidden bg-white border-b border-slate-100 shadow-sm flex flex-col gap-0 relative z-50">
      {/* Search Input Row */}
      <div className="px-4 pt-3.5 pb-2.5">
        <form onSubmit={handleSearch} className="relative w-full group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none z-10 transition-colors group-focus-within:text-orange-500" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search anything..."
            className="w-full pl-11 pr-16 h-[52px] bg-slate-100 border-2 border-transparent rounded-2xl text-[15px] font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:bg-white focus:ring-4 focus:ring-orange-500/10 transition-all shadow-inner"
          />
          <button
            type="submit"
            className="absolute right-1.5 top-1.5 bottom-1.5 w-[56px] bg-gradient-to-r from-orange-500 to-[#FF4500] text-white rounded-xl font-black text-sm active:scale-95 transition-all hover:shadow-lg hover:shadow-orange-500/30 flex items-center justify-center z-10 shadow-sm"
          >
            Go
          </button>
        </form>
      </div>



      {/* ── County Selection Modal ── */}
      {isCountyModalOpen && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-white">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-4 bg-white border-b border-slate-100 shadow-sm shrink-0">
            <div>
              <h3 className="font-black text-slate-800 text-[17px] leading-tight">Select County</h3>
              <p className="text-[11px] text-slate-400 mt-0.5">47 counties in Kenya</p>
            </div>
            <button
              onClick={() => setIsCountyModalOpen(false)}
              className="p-2 bg-slate-100 rounded-full active:scale-95 transition-transform"
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* Search Box */}
          <div className="px-4 py-3 bg-white border-b border-slate-100 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                ref={countySearchRef}
                type="text"
                value={countySearch}
                onChange={e => setCountySearch(e.target.value)}
                placeholder="Search county..."
                className="w-full pl-9 pr-9 h-10 bg-slate-100 rounded-xl text-[14px] text-slate-700 placeholder-slate-400 border border-transparent focus:outline-none focus:border-orange-400 focus:bg-white transition-all"
              />
              {countySearch && (
                <button
                  onClick={() => setCountySearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* County List */}
          <div className="flex-1 overflow-y-auto">
            {/* All Kenya option */}
            {!countySearch && (
              <>
                <button
                  onClick={() => handleSelectCounty(null)}
                  className={`w-full flex items-center justify-between px-5 py-3.5 border-b border-slate-50 transition-colors ${
                    !selectedCounty ? 'bg-orange-50' : 'active:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🇰🇪</span>
                    <span className={`text-[15px] font-semibold ${!selectedCounty ? 'text-orange-600' : 'text-slate-700'}`}>
                      All Kenya
                    </span>
                  </div>
                  {!selectedCounty && <span className="w-2 h-2 rounded-full bg-orange-500" />}
                </button>
              </>
            )}

            {/* County entries */}
            {filteredCounties.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-[14px]">
                No county found for "<span className="font-semibold">{countySearch}</span>"
              </div>
            ) : (
              filteredCounties.map((county, idx) => (
                <button
                  key={county.name}
                  onClick={() => handleSelectCounty(county.name)}
                  className={`w-full flex items-center justify-between px-5 py-3.5 border-b border-slate-50 transition-colors ${
                    selectedCounty === county.name ? 'bg-orange-50' : 'active:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {/* Rank badge for top 4 */}
                    {idx < 4 && !countySearch ? (
                      <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 text-[10px] font-black flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-400 text-[10px] font-bold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                    )}
                    <span className={`text-[15px] font-semibold ${selectedCounty === county.name ? 'text-orange-600' : 'text-slate-700'}`}>
                      {county.name}
                    </span>
                    {idx < 4 && !countySearch && (
                      <span className="text-[10px] bg-orange-100 text-orange-500 font-bold px-1.5 py-0.5 rounded-full">Top</span>
                    )}
                  </div>
                  {selectedCounty === county.name
                    ? <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                    : <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                  }
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* ── Area / Sub-County Selection Modal ── */}
      {isAreaModalOpen && (
        <div className="fixed inset-0 z-[100] flex flex-col bg-white">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-4 bg-white border-b border-slate-100 shadow-sm shrink-0">
            <div>
              <h3 className="font-black text-slate-800 text-[17px] leading-tight">Select Area</h3>
              <p className="text-[11px] text-orange-500 font-semibold mt-0.5">{selectedCounty}</p>
            </div>
            <button
              onClick={() => setIsAreaModalOpen(false)}
              className="p-2 bg-slate-100 rounded-full active:scale-95 transition-transform"
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* Search Box */}
          <div className="px-4 py-3 bg-white border-b border-slate-100 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                ref={areaSearchRef}
                type="text"
                value={areaSearch}
                onChange={e => setAreaSearch(e.target.value)}
                placeholder="Search area..."
                className="w-full pl-9 pr-9 h-10 bg-slate-100 rounded-xl text-[14px] text-slate-700 placeholder-slate-400 border border-transparent focus:outline-none focus:border-orange-400 focus:bg-white transition-all"
              />
              {areaSearch && (
                <button
                  onClick={() => setAreaSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Area List */}
          <div className="flex-1 overflow-y-auto">
            {/* All county option */}
            {!areaSearch && (
              <button
                onClick={() => handleSelectSubCounty('')}
                className={`w-full flex items-center justify-between px-5 py-3.5 border-b border-slate-50 transition-colors ${
                  !selectedSubCounty ? 'bg-orange-50' : 'active:bg-slate-50'
                }`}
              >
                <span className={`text-[15px] font-semibold ${!selectedSubCounty ? 'text-orange-600' : 'text-slate-700'}`}>
                  All {selectedCounty}
                </span>
                {!selectedSubCounty && <span className="w-2 h-2 rounded-full bg-orange-500" />}
              </button>
            )}

            {/* Sub-county entries */}
            {filteredSubCounties.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-[14px]">
                No area found for "<span className="font-semibold">{areaSearch}</span>"
              </div>
            ) : (
              filteredSubCounties.map(sub => (
                <button
                  key={sub.name}
                  onClick={() => handleSelectSubCounty(sub.name)}
                  className={`w-full flex items-center justify-between px-5 py-3.5 border-b border-slate-50 transition-colors ${
                    selectedSubCounty === sub.name ? 'bg-orange-50' : 'active:bg-slate-50'
                  }`}
                >
                  <span className={`text-[15px] font-semibold ${selectedSubCounty === sub.name ? 'text-orange-600' : 'text-slate-700'}`}>
                    {sub.name}
                  </span>
                  {selectedSubCounty === sub.name
                    ? <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                    : <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                  }
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
